import { i18n } from "../../i18n/i18n";

import React, { Component } from 'react'
import { isEmpty } from 'lodash'
import dateUtil from '../../utils/date.util'
import purchaseOrderService from '../../services/purchaseOrder.service'
import itemService from '../../services/item.service'
import printService from '../../services/print.service'
import geolocationService from '../../services/geolocation.service'
import confirmService from '../../services/confirm.service'
import alertService from '../../services/alert.service'
import purchaseOrderApi, {
  PurchaseOrderCreateRequest, PurchaseOrderData
} from '../../apis/purchaseOrder.api'
import itemApi, { ItemData } from '../../apis/item.api'
import { VendorData } from '../../apis/vendor.api'
import palletApi from '../../apis/pallet.api'
import receivingApi, { ReceivingCreateRequest } from '../../apis/receiving.api'
import inventoryApi from '../../apis/inventory.api'
import { ItemType } from '../../apis/ItemType.api'
import { ConfirmParams } from '../../components/Confirm/Confirm.state'
import Card from '../../components/Card'
import PageHeading from '../../components/PageHeading'
import Row from '../../components/Row/Row'
import { SearchPickerOption } from '../../components/SearchPicker'
import CardButton from '../../components/CardButton'
import FormFields from './FormFields'

interface Props {
  onItemSelect: (item: ItemData, vendor: VendorData | null) => void
  onReset: () => void
}

interface State {
  interShip: boolean
  quickPO: boolean
  item: string
  itemId: string
  purchaseOrder: string
  purchaseOrderId: number | null
  quantity: string
  pallet: string
  palletId: number | null
  reference: string
  sku: string
  productId: string
  batchLotNumber: string
  billOfLading: string
  notes: string,
  purchaseOrders: PurchaseOrderData[]
  vendor: VendorData | null
  items: ItemData[]
  latitude: number | null
  longitude: number | null
  printPalletOnly: boolean
}

export default class ReceivingCreateForm extends Component<Props, State> {
  initialState: State = {
    interShip: false,
    quickPO: false,
    item: '',
    itemId: '',
    purchaseOrder: '',
    purchaseOrderId: null,
    quantity: '',
    pallet: '',
    palletId: null,
    reference: '',
    sku: '',
    productId: '',
    batchLotNumber: '',
    billOfLading: '',
    notes: '',
    purchaseOrders: [],
    vendor: null,
    items: [],
    latitude: null,
    longitude: null,
    printPalletOnly: false
  }

  constructor (props: Props) {
    super(props)
    this.state = this.initialState
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleInputScan = this.handleInputScan.bind(this)
    this.handleSearch = this.handleSearch.bind(this)
    this.handleSearchSelect = this.handleSearchSelect.bind(this)
    this.handleSearchBlur = this.handleSearchBlur.bind(this)
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this)
    this.handleCreateClick = this.handleCreateClick.bind(this)
  }

  get purchaseOrderSearchOptions () {
    return purchaseOrderService.searchOptions(this.state.purchaseOrders)
  }

  get itemSearchOptions () {
    return itemService.searchOptions(this.state.items)
  }

  get itemSelectOptions () {
    return itemService.selectOptions(this.state.items)
  }

  get createPayload () {
    const { interShip, reference, latitude, longitude } = this.state
    const { palletId, purchaseOrderId, itemId, quantity, sku, productId, batchLotNumber, billOfLading, notes } = this.state
    const payload: ReceivingCreateRequest = {}
    payload.interShip = interShip
    if (reference) payload.reference = reference
    if(sku) payload.sku = sku
    if(productId) payload.productId = productId
    if(batchLotNumber) payload.batchLotNumber = batchLotNumber
    if(billOfLading) payload.billOfLading = billOfLading
    if(notes) payload.notes = notes
    if (latitude) payload.latitude = latitude
    if (longitude) payload.longitude = longitude
    if (purchaseOrderId) payload.purchaseOrder = purchaseOrderId
    if (interShip) {
      if (palletId) payload.pallet = palletId
    } else {
      if (itemId) payload.item = Number(itemId)
      if (quantity) payload.quantity = Number(quantity)
    }
    return payload
  }

  handleSearch (name: string, code: string) {
    if (name === 'purchaseOrder') {
      purchaseOrderApi.searchByCode(code, purchaseOrders => {
        if (!purchaseOrders) return
        this.setState({ purchaseOrders })
      })
    } else if (name === 'item') {
      const types: ItemType[] = ['receivable']
      itemApi.searchByCode(code, types, items => {
        if (!items) return
        this.setState({ items })
      })
    }
  }

  handleItemChange (itemId: number, items: ItemData[]) {
    const { vendor } = this.state
    const item = itemService.find(items, itemId)
    if (!item) return
    this.setState({ quantity: String(item.cpp) })
    this.props.onItemSelect(item, vendor)
  }

  readPallet (code: string) {
    palletApi.readByCode(code, pallet => {
      if (!pallet) {
        this.setState({ pallet: '', palletId: null })
        return
      }
      this.setState({ palletId: pallet.id })
      this.handleItemChange(pallet.itemId, this.state.items)
    })
  }

  handleInputChange (name: string, value: string) {
    if (name === 'purchaseOrder') {
      this.setState({ purchaseOrderId: null, purchaseOrder: value })
    } else if (name === 'item') {
      const item = value
      this.setState({ itemId: '', item })
    } else if (name === 'itemId') {
      const itemId = value
      this.setState({ itemId })
      this.handleItemChange(Number(itemId), this.state.items)
    } else if (name === 'pallet') {
      this.setState({ pallet: value })
    } else if (name === 'quantity') {
      this.setState({ quantity: value })
    } else if (name === 'reference') {
      this.setState({ reference: value })
    } else if(name === 'sku'){
      this.setState({sku: value})
    } else if(name === 'productId'){
      this.setState({productId: value})
    } else if(name === 'batchLotNumber') {
      this.setState({batchLotNumber: value})
    } else if(name === 'billOfLading') {
      this.setState({billOfLading: value})
    } else if(name === 'notes') {
      this.setState({notes: value})
    }
  }

  handleInputScan (name: string, value: string) {
    if (name === 'pallet') {
      this.readPallet(value)
    }
  }

  listPurchaseOrderItems () {
    const { purchaseOrderId } = this.state
    if (!purchaseOrderId) return
    purchaseOrderApi.listItems(purchaseOrderId, items => {
      if (!items) return
      this.setState({ items })
    })
  }

  handleSearchSelect (name: string, option: SearchPickerOption) {
    if (name === 'purchaseOrder') {
      const purchaseOrderId = option.id as number
      const purchaseOrder = purchaseOrderService.find(this.state.purchaseOrders, purchaseOrderId)
      this.setState({ purchaseOrderId, purchaseOrders: [] }, () => {
        this.listPurchaseOrderItems()
      })
      if (
        !purchaseOrder ||
        !purchaseOrder.vendorSite ||
        !purchaseOrder.vendorSite.contact ||
        !purchaseOrder.vendorSite.contact.vendor
      ) return
      this.setState({ vendor: purchaseOrder.vendorSite.contact.vendor })
    } else if (name === 'item') {
      const itemId = option.id as number
      this.handleItemChange(itemId, this.state.items)
      this.setState({ itemId: String(itemId), items: [] })
    }
  }

  handleSearchBlur (name: string) {
    if (name === 'purchaseOrder') {
      this.setState({ purchaseOrders: [] })
    } else if (name === 'item') {
      this.setState({ items: [] })
    }
  }

  handleCheckboxChange (name: string) {
    if (name === 'interShip') {
      const { interShip } = this.state
      this.reset(() => {
        this.setState(state => {
          return { interShip: !interShip }
        })
      })
    } else if (name === 'quickPO') {
      const { quickPO } = this.state
      this.reset(() => {
        this.setState(state => {
          return { quickPO: !quickPO }
        })
      })
    } else if (name === 'printPalletOnly') {
      this.setState(state => {
        return { printPalletOnly: !state.printPalletOnly }
      })
    }
  }

  reset (callback?: () => void) {
    this.setState(this.initialState, () => {
      this.readGeolocation()
      if (callback) callback()
    })
    this.props.onReset()
  }

  print (id: number, palletId: number, callback: () => void) {
    const { printPalletOnly } = this.state
    if (printPalletOnly) {
      palletApi.print(palletId, markups => {
        if (!markups) return
        printService.printMarkups(markups)
        callback()
      })
    } else {
      inventoryApi.printLabels(id, markups => {
        if (!markups) return
        printService.printMarkups(markups)
        callback()
      })
    }
  }

  createPurchaseOrder () {
    return new Promise((resolve, reject) => {
      const { purchaseOrder, itemId, quantity } = this.state
      const deliveryAt = dateUtil.toUtc(new Date())
      const payload: PurchaseOrderCreateRequest = {}
      if (purchaseOrder) payload.purchaseOrder = purchaseOrder
      if (itemId && quantity) {
        const items = [{
          id: Number(itemId),
          quantity: Number(quantity)
        }]
        payload.items = items
      }
      payload.line = '0'
      payload.deliveryAt = deliveryAt
      purchaseOrderApi.create(payload, purchaseOrder => {
        if (!purchaseOrder) return resolve(false)
        this.setState({ purchaseOrderId: purchaseOrder.id }, () => {
          resolve(true)
        })
      })
    })
  }

  createReceiving () {
    const { interShip } = this.state
    receivingApi.create(this.createPayload, receiving => {
      if (!receiving || !receiving.pallets) return
      if (!interShip) {
        const palletId = receiving.pallets[0].id
        this.print(receiving.id, palletId, () => {
          alertService.success('Item(s) successfully received')
        })
      } else {
        alertService.success('Item(s) successfully received')
      }
      this.reset()
    })
  }

  async handleCreateConfirm () {
    const { quickPO, purchaseOrder } = this.state
    if (quickPO) {
      purchaseOrderApi.exist(purchaseOrder, async (purchaseOrder) => {
        if (isEmpty(purchaseOrder)) {
          const success = await this.createPurchaseOrder()
          if (success) this.createReceiving()
        } else {
          this.setState({ purchaseOrderId: purchaseOrder.id }, () => {
            this.createReceiving()
          })
        }
      })
    } else this.createReceiving()
  }

  handleCreateClick () {
    const confirm: ConfirmParams = {
      id: 1,
      question: i18n('Please review receving details above and then confirm'),
      confirmText: i18n('Confirm'),
      cancelText: i18n('Cancel'),
      onConfirm: () => this.handleCreateConfirm()
    }
    confirmService.push(confirm)
  }

  readGeolocation () {
    geolocationService.access(coordinate => {
      if (!coordinate) return
      const { latitude, longitude } = coordinate
      this.setState({ latitude, longitude })
    })
  }

  componentDidMount () {
    this.readGeolocation()
  }

  render () {
    return (
      <Card>
        <PageHeading
          text={i18n("Create Receiving")}
          doc='receivings-create-inter-ship-receiving' />
        <Row column={2}>
          <FormFields
            interShip={this.state.interShip}
            quickPO={this.state.quickPO}
            purchaseOrder={this.state.purchaseOrder}
            pallet={this.state.pallet}
            item={this.state.item}
            itemId={this.state.itemId}
            quantity={this.state.quantity}
            reference={this.state.reference}
            sku={this.state.sku}
            productId={this.state.productId}
            batchLotNumber={this.state.batchLotNumber}
            billOfLading={this.state.billOfLading}
            notes={this.state.notes}
            printPalletOnly={this.state.printPalletOnly}
            purchaseOrderSearchOptions={this.purchaseOrderSearchOptions}
            itemSearchOptions={this.itemSearchOptions}
            itemSelectOptions={this.itemSelectOptions}
            handleSearch={this.handleSearch}
            handleSearchSelect={this.handleSearchSelect}
            handleSearchBlur={this.handleSearchBlur}
            handleInputChange={this.handleInputChange}
            handleInputScan={this.handleInputScan}
            handleCheckboxChange={this.handleCheckboxChange} />
        </Row>
        <CardButton
          proceedText={i18n("Create")}
          cancelText={i18n("Cancel")}
          onProceedClick={this.handleCreateClick} />
      </Card>
    )
  }
}
